import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader, Modal, TextInput, UnstyledButton } from '@mantine/core';
import { Button } from '../../../../components/Button';
import { AdminPaymentlineView } from '../../../../services/admin-payments/admin-payments.service';
import { resolveTip } from 'shared-models/browser';
import { orderlineDisplayName, OrderlineView } from '../../../../services/orderlines/orderlines.service';
import { OrderView } from '../../../../services/orders/orders.service';
import { ordersService, OrderPrintLogView } from '../../../../services/orders/orders.service';
import { ReportView } from '../../../../services/reports/reports.service';
import { receiptService, type ReceiptView } from '../../../../services/receipt/receipt.service';
import { TransactionReceiptContent } from '../../../PosShellPage/components/receipt/transaction-receipt/TransactionReceiptContent';
import { loadReceiptFont } from '../../../PosShellPage/components/receipt/shared/receipt.utils';
import receiptClasses from '../../../PosShellPage/components/receipt/transaction-receipt/transaction-receipt.module.scss';
import { TotalsMode } from '../../shared/types/dashboard-page.types';
import { asCurrency, asHour, computeGlobalDiscountAmount, sum } from '../../shared/utils/dashboard-page.utils';
import { creditMethodLabel } from '../../../../utils/credit-method-label';
import { downloadBreakdownOrdersExcel } from './breakdown-row-export.utils';
import { printSummaryReceiptPdf } from './summary-receipt-pdf.util';
import classes from './breakdown-row-modal.module.scss';

type SalesOrderRow = {
  orderId: number;
  tenantId: string;
  orderCode: string;
  tableLabel: string;
  waiterName: string;
  amount: number;
  tip: number;
  openedAt: string;
  updatedAt: string;
  dinersCount: number;
  status: 'open' | 'closed';
};

type ReportGroupTotals = { food: number; beverages: number };
type OrderAdjustments = { canceledItemsCount: number; discounts: number };
type ReportGroupOrderlineView = OrderlineView & { reportGroup?: 'food' | 'beverages' | null };

export type SalesSummaryModalContext = {
  selectedCard: { key: TotalsMode; title: string; value: number; count?: number };
  shiftName?: string;
  report: ReportView | null;
  orders: OrderView[];
  closedOrderDinersByCode: Record<string, number>;
  payments: AdminPaymentlineView[];
};

export type BreakdownRowModalProps = {
  selectedSummary: SalesSummaryModalContext | null;
  onClose: () => void;
};

const getStatusScopedPayments = (
  cardMode: TotalsMode,
  rows: AdminPaymentlineView[],
): AdminPaymentlineView[] => {
  const doneRows = rows.filter((row) => row.status === 'done');
  if (cardMode === 'openOrders') {
    return doneRows.filter((row) => row.orderStatus === 'open');
  }
  if (cardMode === 'closedOrders' || cardMode === 'closedShift') {
    return doneRows.filter((row) => row.orderStatus === 'closed');
  }
  return doneRows;
};

const toOpenOrderRows = (rows: OrderView[]): SalesOrderRow[] => rows.map((order) => ({
  orderId: order.id,
  tenantId: order.tenantId,
  orderCode: order.orderCode,
  tableLabel: order.tableNumber ? `שולחן ${order.tableNumber}` : (order.orderType === 'take_away' ? 'טייק אווי' : 'ללא שולחן'),
  waiterName: order.openedByName?.trim() || '—',
  amount: order.totalAmount ?? 0,
  tip: 0,
  openedAt: order.createdAt,
  updatedAt: order.lastActivityAt ?? order.updatedAt ?? order.createdAt,
  dinersCount: order.dinersCount ?? 0,
  status: 'open',
}));

const collectedTip = (payment: AdminPaymentlineView): number =>
  (payment.globalTipCollected ?? 0) + resolveTip(payment.tipInputMode ?? null, payment.tipInputValue ?? null, payment.baseAmount);

const toClosedPaymentRows = (rows: AdminPaymentlineView[], dinersByCode: Record<string, number>): SalesOrderRow[] => {
  const grouped = new Map<string, SalesOrderRow>();
  rows.forEach((payment) => {
    const current = grouped.get(payment.orderCode);
    const waiterName = payment.openedByName ?? payment.createdByName ?? payment.closedByName ?? payment.changedByName ?? '—';
    if (!current) {
      grouped.set(payment.orderCode, {
        orderId: payment.orderId,
        tenantId: payment.tenantId,
        orderCode: payment.orderCode,
        tableLabel: payment.tableNumber ? `שולחן ${payment.tableNumber}` : 'ללא שולחן',
        waiterName,
        amount: payment.baseAmount,
        tip: collectedTip(payment),
        openedAt: payment.createdAt,
        updatedAt: payment.updatedAt,
        dinersCount: dinersByCode[payment.orderCode] ?? 0,
        status: 'closed',
      });
      return;
    }
    current.amount += payment.baseAmount;
    current.tip += collectedTip(payment);
    if (!current.waiterName || current.waiterName === '—') {
      current.waiterName = waiterName;
    }
    if (new Date(payment.updatedAt).getTime() > new Date(current.updatedAt).getTime()) {
      current.updatedAt = payment.updatedAt;
    }
  });
  return [...grouped.values()];
};

const printKindLabel = (kind: OrderPrintLogView['kind']): string => {
  if (kind === 'reprint') {
    return 'הדפסה חוזרת';
  }
  if (kind === 'receipt_print') {
    return 'הדפסת חשבון';
  }
  if (kind === 'red_fire') {
    return 'RED - דילוג';
  }
  return 'הדפסת מטבח';
};

const paymentMethodLabel = (row: AdminPaymentlineView): string =>
  creditMethodLabel(row.method, row.creditSource, row.terminalName);

const displayOrderCode = (orderCode: string): string => orderCode.split('/').slice(-2).join('/');

const calculateOrderReportGroupTotals = (order: OrderView, orderlines: ReportGroupOrderlineView[]): ReportGroupTotals => {
  const subtotals = orderlines.reduce<Record<'food' | 'beverages' | 'other', number>>((totals, line) => {
    const group = line.reportGroup ?? 'other';
    totals[group] += line.unitPrice * line.quantity - (line.discountInput ?? 0);
    return totals;
  }, { food: 0, beverages: 0, other: 0 });
  const subtotal = sum(Object.values(subtotals));
  const discount = computeGlobalDiscountAmount(subtotal, order.discountMode, order.discountInput);

  return {
    food: subtotal > 0 ? Math.max(0, subtotals.food - (discount * subtotals.food) / subtotal) : 0,
    beverages: subtotal > 0 ? Math.max(0, subtotals.beverages - (discount * subtotals.beverages) / subtotal) : 0,
  };
};

const calculateOrderAdjustments = (order: OrderView, orderlines: OrderlineView[], canceledOrderlines: OrderlineView[]): OrderAdjustments => {
  const canceledItemsCount = sum(canceledOrderlines.map((line) => line.quantity));
  const itemDiscounts = sum(orderlines.map((line) => line.discountInput ?? 0));
  const netItemsTotal = sum(orderlines.map((line) => line.unitPrice * line.quantity - (line.discountInput ?? 0)));
  const globalDiscount = computeGlobalDiscountAmount(netItemsTotal, order.discountMode, order.discountInput);

  return { canceledItemsCount, discounts: itemDiscounts + globalDiscount };
};

export const BreakdownRowModal: React.FC<BreakdownRowModalProps> = ({
  selectedSummary,
  onClose,
}) => {
  const [query, setQuery] = useState('');
  const [orderProductSearchTextById, setOrderProductSearchTextById] = useState<Record<number, string>>({});
  const [orderReportGroupTotalsById, setOrderReportGroupTotalsById] = useState<Record<number, ReportGroupTotals>>({});
  const [orderAdjustmentsById, setOrderAdjustmentsById] = useState<Record<number, OrderAdjustments>>({});
  const [isHydratingProductSearch, setIsHydratingProductSearch] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<SalesOrderRow | null>(null);
  const [orderDetailsLoading, setOrderDetailsLoading] = useState(false);
  const [orderDetailsError, setOrderDetailsError] = useState<string | null>(null);
  const [orderView, setOrderView] = useState<OrderView | null>(null);
  const [orderlines, setOrderlines] = useState<OrderlineView[]>([]);
  const [printLogs, setPrintLogs] = useState<OrderPrintLogView[]>([]);
  const [summaryReceipt, setSummaryReceipt] = useState<ReceiptView | null>(null);
  const receiptPaperRef = React.useRef<HTMLDivElement | null>(null);
  const [expandedPrintLogIds, setExpandedPrintLogIds] = useState<Set<number>>(new Set());
  const orderHydrationInFlightIdsRef = React.useRef(new Set<number>());
  const orderHydrationGenerationRef = React.useRef(0);

  useEffect(() => {
    setQuery('');
  }, [selectedSummary?.selectedCard.key]);

  useEffect(() => {
    setSelectedOrder(null);
    setOrderView(null);
    setOrderlines([]);
    setPrintLogs([]);
    setSummaryReceipt(null);
    setExpandedPrintLogIds(new Set());
    setOrderDetailsError(null);
    setOrderDetailsLoading(false);
    setOrderProductSearchTextById({});
    setOrderReportGroupTotalsById({});
    setOrderAdjustmentsById({});
    setIsHydratingProductSearch(false);
    orderHydrationInFlightIdsRef.current.clear();
    orderHydrationGenerationRef.current += 1;
  }, [selectedSummary?.selectedCard.key]);

  const togglePrintLogItems = (logId: number) => {
    setExpandedPrintLogIds((previous) => {
      const next = new Set(previous);
      if (next.has(logId)) {
        next.delete(logId);
      } else {
        next.add(logId);
      }
      return next;
    });
  };

  useEffect(() => {
    if (!selectedOrder) {
      return;
    }

    let cancelled = false;
    const loadOrderDetails = async () => {
      setOrderDetailsLoading(true);
      setOrderDetailsError(null);
      try {
        const details = await ordersService.getDashboardOrderDetails(selectedOrder.tenantId, selectedOrder.orderId);

        if (cancelled) {
          return;
        }
        setOrderView(details.order);
        setOrderlines(details.orderlines);
        setPrintLogs(details.printLogs);
      } catch {
        if (!cancelled) {
          setOrderDetailsError('טעינת פרטי ההזמנה נכשלה');
          setOrderView(null);
          setOrderlines([]);
          setPrintLogs([]);
        }
      } finally {
        if (!cancelled) {
          setOrderDetailsLoading(false);
        }
      }
    };

    void loadOrderDetails();

    // The receipt is a separate read: a failure here must not blank the details panel.
    const loadSummaryReceipt = async () => {
      setSummaryReceipt(null);
      try {
        const receipt = await receiptService.getDashboardReceipt(selectedOrder.tenantId, selectedOrder.orderId);
        if (!cancelled) {
          setSummaryReceipt(receipt);
          // Puts the Noto Hebrew <link> in the head so the print iframe can clone it.
          void loadReceiptFont().catch(() => undefined);
        }
      } catch {
        if (!cancelled) {
          setSummaryReceipt(null);
        }
      }
    };

    void loadSummaryReceipt();

    return () => {
      cancelled = true;
    };
  }, [selectedOrder]);

  const scopedDonePayments = useMemo(
    () => (selectedSummary ? getStatusScopedPayments(selectedSummary.selectedCard.key, selectedSummary.payments) : []),
    [selectedSummary],
  );

  const baseOrderRows = useMemo(() => {
    if (!selectedSummary) {
      return [] as SalesOrderRow[];
    }

    const openRows = toOpenOrderRows(selectedSummary.orders);
    const closedRows = toClosedPaymentRows(
      selectedSummary.payments.filter((row) => row.status === 'done' && row.orderStatus === 'closed'),
      selectedSummary.closedOrderDinersByCode,
    );

    return (
      selectedSummary.selectedCard.key === 'openOrders'
        ? openRows
        : selectedSummary.selectedCard.key === 'closedOrders' || selectedSummary.selectedCard.key === 'closedShift'
          ? closedRows
          : [...closedRows, ...openRows]
    ).sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
  }, [selectedSummary]);

  useEffect(() => {
    if (!selectedSummary) {
      setIsHydratingProductSearch(false);
      return;
    }

    const missingOrderIds = baseOrderRows
      .map((row) => row.orderId)
      .filter((orderId, index, all) => all.indexOf(orderId) === index)
      .filter((orderId) => (
        orderProductSearchTextById[orderId] === undefined
        || orderReportGroupTotalsById[orderId] === undefined
        || orderAdjustmentsById[orderId] === undefined
      ))
      .filter((orderId) => !orderHydrationInFlightIdsRef.current.has(orderId));

    if (missingOrderIds.length === 0) {
      setIsHydratingProductSearch(false);
      return;
    }

    const generation = orderHydrationGenerationRef.current;
  setIsHydratingProductSearch(true);
    missingOrderIds.forEach((orderId) => orderHydrationInFlightIdsRef.current.add(orderId));

    const hydrateProductSearch = async () => {
      try {
        const entries = await Promise.all(
          missingOrderIds.map(async (orderId) => {
            const row = baseOrderRows.find((candidate) => candidate.orderId === orderId);
            if (!row) {
              return { orderId, searchableProducts: '', reportGroupTotals: { food: 0, beverages: 0 }, adjustments: { canceledItemsCount: 0, discounts: 0 } };
            }
            try {
              const details = await ordersService.getDashboardOrderDetails(row.tenantId, orderId);
              const searchableProducts = details.orderlines
                .map((line) => orderlineDisplayName(line).toLowerCase())
                .join(' ');
              return {
                orderId,
                searchableProducts,
                reportGroupTotals: calculateOrderReportGroupTotals(details.order, details.orderlines),
                adjustments: calculateOrderAdjustments(details.order, details.orderlines, details.canceledOrderlines ?? []),
              };
            } catch {
              return { orderId, searchableProducts: '', reportGroupTotals: { food: 0, beverages: 0 }, adjustments: { canceledItemsCount: 0, discounts: 0 } };
            }
          }),
        );

        if (generation !== orderHydrationGenerationRef.current) {
          return;
        }

        setOrderProductSearchTextById((previous) => {
          const next = { ...previous };
          for (const entry of entries) {
            next[entry.orderId] = entry.searchableProducts;
          }
          return next;
        });
        setOrderReportGroupTotalsById((previous) => {
          const next = { ...previous };
          for (const entry of entries) {
            next[entry.orderId] = entry.reportGroupTotals;
          }
          return next;
        });
        setOrderAdjustmentsById((previous) => {
          const next = { ...previous };
          for (const entry of entries) {
            next[entry.orderId] = entry.adjustments;
          }
          return next;
        });
        setIsHydratingProductSearch(false);
      } finally {
        missingOrderIds.forEach((orderId) => orderHydrationInFlightIdsRef.current.delete(orderId));
      }
    };

    void hydrateProductSearch();

  }, [baseOrderRows, orderAdjustmentsById, orderProductSearchTextById, orderReportGroupTotalsById, selectedSummary]);

  const orderRows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
      return baseOrderRows;
    }

    return baseOrderRows.filter((row) => {
      const orderHaystack = `${row.orderCode} ${row.tableLabel} ${row.waiterName}`.toLowerCase();
      if (orderHaystack.includes(needle)) {
        return true;
      }
      const productHaystack = orderProductSearchTextById[row.orderId] ?? '';
      return productHaystack.includes(needle);
    });
  }, [baseOrderRows, orderProductSearchTextById, query]);

  const summary = useMemo(() => {
    if (!selectedSummary) {
      return null;
    }
    const report = selectedSummary.report;
    const sales = sum(orderRows.map((row) => row.amount));
    const tipsTotal = sum(orderRows.map((row) => row.tip));
    const ordersCount = orderRows.length;
    // Every order with content counts its guests, matching the מכירות table and the Z report even
    // when the order settled nothing (OTH / fully discounted).
    const dinersCount = sum(orderRows.map((row) => row.dinersCount));
    const avgPerDiner = dinersCount > 0 ? sales / dinersCount : 0;
    const filteredReportGroupTotals = orderRows.reduce<ReportGroupTotals>((totals, row) => {
      const rowTotals = orderReportGroupTotalsById[row.orderId] ?? { food: 0, beverages: 0 };
      return {
        food: totals.food + rowTotals.food,
        beverages: totals.beverages + rowTotals.beverages,
      };
    }, { food: 0, beverages: 0 });
    let food: number;
    let beverages: number;

    if (query.trim()) {
      food = filteredReportGroupTotals.food;
      beverages = filteredReportGroupTotals.beverages;
    } else if (selectedSummary.selectedCard.key === 'openOrders') {
      food = report?.openOrdersSalesByReportGroup.food ?? 0;
      beverages = report?.openOrdersSalesByReportGroup.beverages ?? 0;
    } else if (selectedSummary.selectedCard.key === 'ordersTotal') {
      food = (report?.salesByReportGroup.food ?? 0) + (report?.openOrdersSalesByReportGroup.food ?? 0);
      beverages = (report?.salesByReportGroup.beverages ?? 0) + (report?.openOrdersSalesByReportGroup.beverages ?? 0);
    } else {
      food = report?.salesByReportGroup.food ?? 0;
      beverages = report?.salesByReportGroup.beverages ?? 0;
    }

    const totalWithTips = sales + tipsTotal;
    const cash = sum(scopedDonePayments.filter((row) => row.method === 'cash').map((row) => row.baseAmount));
    const credit = sum(scopedDonePayments.filter((row) => row.method === 'credit').map((row) => row.baseAmount));

    return {
      totalWithTips,
      ordersCount,
      avgPerDiner,
      dinersCount,
      sales,
      tips: tipsTotal,
      food,
      beverages,
      cash,
      credit,
    };
  }, [orderReportGroupTotalsById, orderRows, query, scopedDonePayments, selectedSummary]);

  const selectedOrderPayments = useMemo(() => {
    if (!selectedSummary || !selectedOrder) {
      return [] as AdminPaymentlineView[];
    }
    return selectedSummary.payments.filter((row) => row.orderCode === selectedOrder.orderCode && row.status === 'done');
  }, [selectedOrder, selectedSummary]);

  const globalDiscount = useMemo(() => {
    if (!orderView || orderView.discountMode == null || orderView.discountInput == null) {
      return null;
    }
    const netItemsTotal = sum(orderlines.map((line) => line.unitPrice * line.quantity - (line.discountInput ?? 0)));
    const amount = computeGlobalDiscountAmount(netItemsTotal, orderView.discountMode, orderView.discountInput);
    return { pct: orderView.discountMode === 'percentage' ? orderView.discountInput : null, amount };
  }, [orderView, orderlines]);

  const exportOrders = () => {
    if (!selectedSummary || !summary) {
      return;
    }
    downloadBreakdownOrdersExcel(selectedSummary.shiftName?.trim() || 'פירוט הזמנות', {
      ordersCount: summary.ordersCount,
      sales: summary.sales,
      totalWithTips: summary.totalWithTips,
      tips: summary.tips,
      dinersCount: summary.dinersCount,
      avgPerDiner: summary.avgPerDiner,
      food: summary.food,
      beverages: summary.beverages,
    }, orderRows.map((row) => {
      const adjustments = orderAdjustmentsById[row.orderId];
      const average = row.dinersCount > 0 ? row.amount / row.dinersCount : '—';
      return {
        status: row.status === 'open' ? 'פתוחה' : 'סגורה',
        orderCode: displayOrderCode(row.orderCode),
        closingTime: row.status === 'closed' ? asHour(row.updatedAt) : '-',
        table: row.tableLabel,
        openingTime: asHour(row.openedAt),
        waiter: row.waiterName,
        sales: row.amount,
        totalWithTips: row.amount + row.tip,
        tips: row.tip,
        dinersCount: row.dinersCount > 0 ? row.dinersCount : '—',
        average,
        canceledItemsCount: adjustments?.canceledItemsCount || '-',
        discounts: adjustments?.discounts || '-',
      };
    }));
  };

  const downloadSummaryReceipt = () => {
    if (!selectedOrder || !summaryReceipt) {
      return;
    }
    printSummaryReceiptPdf(receiptPaperRef.current, `קבלה מסכמת - הזמנה ${displayOrderCode(selectedOrder.orderCode)}`);
  };

  return (
    <Modal
      opened={Boolean(selectedSummary)}
      onClose={onClose}
      title={(
        <div className={classes.modalTitleRow}>
          <span>פירוט מכירות</span>
          <Button
            variant="cancel"
            className={classes.exportButton}
            onClick={exportOrders}
            dataTestId="dashboard-breakdown-export-excel"
          >
            EXCEL
          </Button>
        </div>
      )}
      size="58rem"
      classNames={{
        inner: classes.modalInner,
        content: classes.modalContent,
        header: classes.modalHeader,
        title: classes.modalTitle,
        body: classes.modalBody,
        close: classes.modalClose,
      }}
      centered
      closeButtonProps={{ 'data-testid': 'dashboard-breakdown-close' } as Record<string, string>}
    >
      {selectedSummary && summary ? (
        <div className={classes.dialogBody}>
          <div className={classes.searchRow}>
            <TextInput
              classNames={{ root: classes.searchInputRoot, input: classes.searchInput }}
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              placeholder="הקלד שם שולחן, מלצר או מוצר"
              aria-label="חיפוש הזמנות"
              data-testid="dashboard-breakdown-search"
              size="md"
            />
            {isHydratingProductSearch && query.trim() ? <p className={classes.sectionHelp}>מחפש גם בתוך פריטים...</p> : null}
          </div>

          <h3 className={classes.sectionTitle}>סיכום</h3>
          <div className={classes.summaryColumns}>
            <div className={classes.summaryCell}><span>סה"כ עם טיפ</span><strong>{asCurrency(summary.totalWithTips)}</strong></div>
            <div className={classes.summaryCell}><span>סועדים</span><strong>{summary.dinersCount}</strong></div>
            <div className={classes.summaryCell}><span>ממוצע לסועד</span><strong>{asCurrency(summary.avgPerDiner)}</strong></div>
            <div className={classes.summaryCell}><span>טיפ</span><strong>{asCurrency(summary.tips)}</strong></div>
            <div className={classes.summaryCell}><span>מכירות</span><strong>{asCurrency(summary.sales)}</strong></div>
            <div className={classes.summaryCell}><span>הזמנות</span><strong>{summary.ordersCount}</strong></div>
            <div className={classes.summaryCell}><span>אוכל</span><strong>{asCurrency(summary.food)}</strong></div>
            <div className={classes.summaryCell}><span>משקאות</span><strong>{asCurrency(summary.beverages)}</strong></div>
          </div>

          <h3 className={classes.sectionTitle}>הזמנות</h3>
          <p className={classes.sectionHelp}>לחיצה על כרטיס הזמנה תציג את פרטי ההזמנה</p>

          <div className={classes.ordersList}>
            {orderRows.length === 0 ? (
              <div className={classes.emptyState}>לא נמצאו הזמנות עבור החיפוש.</div>
            ) : (
              orderRows.map((row) => {
                const totalWithTip = row.amount + row.tip;
                const salesOnly = row.amount;
                const avgPerDiner = row.dinersCount > 0 ? salesOnly / row.dinersCount : null;

                return (
                  <UnstyledButton
                    key={row.orderCode}
                    className={classes.orderCardButton}
                    onClick={() => setSelectedOrder(row)}
                    data-testid={`dashboard-breakdown-order-${row.orderCode}`}
                  >
                    <article className={classes.orderCard}>
                      <div className={classes.orderMetric}><span className={classes.label}>מזהה הזמנה</span><strong>{displayOrderCode(row.orderCode)}</strong></div>
                      <div className={classes.orderMetric}><span className={classes.label}>שולחן</span><strong className={classes.valueAccent}>{row.tableLabel}</strong></div>
                      <div className={classes.orderMetric}><span className={classes.label}>שעה</span><strong>{asHour(row.updatedAt)}</strong></div>
                      <div className={classes.orderMetric}><span className={classes.label}>מלצר</span><strong>{row.waiterName}</strong></div>

                      <div className={classes.orderMetric}><span className={classes.label}>סה"כ עם טיפ</span><strong>{asCurrency(totalWithTip)}</strong></div>
                      <div className={classes.orderMetric}><span className={classes.label}>סועדים</span><strong>{row.dinersCount > 0 ? row.dinersCount : '—'}</strong></div>
                      <div className={classes.orderMetric}><span className={classes.label}>ממוצע</span><strong>{avgPerDiner === null ? '—' : asCurrency(avgPerDiner)}</strong></div>

                      <div className={classes.orderMetric}><span className={classes.label}>טיפ</span><strong>{asCurrency(row.tip)}</strong></div>
                      <div className={classes.orderMetric}><span className={classes.label}>מכירות</span><strong>{asCurrency(salesOnly)}</strong></div>
                      <div className={classes.orderMetric}><span className={classes.label}>סטטוס</span><strong>{row.status === 'open' ? 'פתוחה' : 'סגורה'}</strong></div>
                    </article>
                  </UnstyledButton>
                );
              })
            )}
          </div>
        </div>
      ) : null}

      <Modal
        opened={Boolean(selectedOrder)}
        onClose={() => setSelectedOrder(null)}
        title={selectedOrder ? (
          <div className={classes.detailsModalTitleRow}>
            <span>{`פרטי הזמנה - ${selectedOrder.tableLabel}`}</span>
            <Button
              variant="cancel"
              className={classes.receiptButton}
              onClick={downloadSummaryReceipt}
              disabled={!summaryReceipt}
              dataTestId="dashboard-breakdown-detail-receipt"
            >
              קבלה מסכמת
            </Button>
          </div>
        ) : 'פרטי הזמנה'}
        size="54rem"
        classNames={{
          content: classes.detailsModalContent,
          header: classes.detailsModalHeader,
          title: classes.detailsModalTitle,
          body: classes.detailsModalBody,
          close: classes.modalClose,
        }}
        centered
        closeButtonProps={{ 'data-testid': 'dashboard-breakdown-detail-close' } as Record<string, string>}
      >
        {selectedOrder ? (
          <div className={classes.detailsGrid}>
            {summaryReceipt ? createPortal(
              <div className={classes.receiptCapture} aria-hidden="true">
                <div ref={receiptPaperRef} className={receiptClasses.offscreenReceiptReady}>
                  <TransactionReceiptContent receipt={summaryReceipt} />
                </div>
              </div>,
              document.body,
            ) : null}
            {orderDetailsLoading ? <div className={classes.detailsLoader}><Loader size="sm" color="gray" /></div> : null}
            {orderDetailsError ? <p className={classes.detailsError}>{orderDetailsError}</p> : null}

            <section className={classes.detailsSummaryCard}>
              <div className={classes.detailsStat}><span>מזהה הזמנה</span><strong>{displayOrderCode(selectedOrder.orderCode)}</strong></div>
              <div className={classes.detailsStat}><span>שולחן</span><strong>{selectedOrder.tableLabel}</strong></div>
              <div className={classes.detailsStat}><span>מלצר</span><strong>{selectedOrder.waiterName}</strong></div>
              <div className={classes.detailsStat}><span>שעה</span><strong>{asHour(selectedOrder.updatedAt)}</strong></div>
              <div className={classes.detailsStat}><span>סועדים</span><strong>{(orderView?.dinersCount ?? selectedOrder.dinersCount) > 0 ? (orderView?.dinersCount ?? selectedOrder.dinersCount) : '—'}</strong></div>
              <div className={classes.detailsStat}><span>סה"כ</span><strong>{asCurrency(selectedOrder.amount)}</strong></div>
              <div className={classes.lastRow}>
              <div className={classes.detailsStat}><span>טיפ</span><strong>{asCurrency(selectedOrder.tip)}</strong></div>
              <div className={classes.detailsStat}><span>הנחה</span><strong>{asCurrency(globalDiscount?.amount ?? 0)}</strong></div>
              </div>
            </section>

            <div className={classes.detailsSectionGroup}>
              <h4 className={classes.detailsSectionTitle}>פריטים</h4>
              <section className={`${classes.detailsSection} ${classes.detailsSectionCardTone}`}>
                {orderlines.length === 0 ? (
                  <p className={classes.detailsEmpty}>אין פריטים להצגה</p>
                ) : (
                  <div className={classes.itemsList}>
                    <div className={classes.itemHeaderRow}>
                      <span className={classes.itemHeaderName}>פריט</span>
                      <span className={classes.itemHeaderValue}>מחיר</span>
                      <span className={classes.itemHeaderValue}>סה"כ</span>
                    </div>
                    {orderlines.map((line) => (
                      <article key={line.id} className={classes.itemRow}>
                        <span className={classes.itemName}>
                          <span className={classes.itemNameLane}>
                            <span className={classes.quantityInline}>{`x ${line.quantity}`}</span>
                            {' '}
                            {orderlineDisplayName(line)}
                          </span>
                        </span>
                        <span className={classes.itemValue}>{asCurrency(line.unitPrice)}</span>
                        <span className={classes.itemValue}>{asCurrency(line.unitPrice * line.quantity - (line.discountInput ?? 0))}</span>
                      </article>
                    ))}
                  </div>
                )}
              </section>
            </div>

            <div className={classes.detailsSectionGroup}>
              <h4 className={classes.detailsSectionTitle}>תשלומים</h4>
              <section className={`${classes.detailsSection} ${classes.detailsSectionCardTone}`}>
                {selectedOrderPayments.length === 0 ? (
                  <p className={classes.detailsEmpty}>אין תשלומים להצגה</p>
                ) : (
                  <div className={classes.detailsTable}>
                    <div className={`${classes.detailsTableHead} ${classes.detailsPaymentsHead}`}>
                      <span>אמצעי תשלום</span><span>סכום</span><span>טיפ</span>
                    </div>
                    {selectedOrderPayments.map((row) => (
                      <div key={row.id} className={`${classes.detailsTableRow} ${classes.detailsPaymentsRow}`}>
                        <span>{paymentMethodLabel(row)}</span>
                        <span>{asCurrency(row.baseAmount)}</span>
                        <span>{asCurrency(collectedTip(row))}</span>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>

            <div className={classes.detailsSectionGroup}>
              <h4 className={classes.detailsSectionTitle}>לוג הדפסות</h4>
              <section className={`${classes.detailsSection} ${classes.detailsSectionCardTone} ${classes.logSectionPlain}`}>
                {printLogs.length === 0 ? (
                  <p className={classes.detailsEmpty}>אין לוגים להצגה</p>
                ) : (
                  <>
                    <div className={`${classes.detailsTableHead} ${classes.detailsLogsHead}`}>
                      <span>זמן</span><span>סוג</span><span>עובד</span><span>מדפסת</span><span>פירוט</span>
                    </div>
                    <div className={classes.logEntries}>
                      {printLogs.map((log) => (
                        <article key={log.id} className={classes.logEntry}>
                          <div className={`${classes.detailsTableRow} ${classes.detailsLogsRow}`}>
                            <span>{asHour(log.occurredAt ?? log.createdAt)}</span>
                            <span>{printKindLabel(log.kind)}</span>
                            <span>{log.workerName ?? '—'}</span>
                            <span>{log.printerName ?? '—'}</span>
                            <span>
                              {log.items.length > 0 ? (
                                <UnstyledButton
                                  className={classes.logDetailsToggle}
                                  onClick={() => togglePrintLogItems(log.id)}
                                  data-testid={`dashboard-breakdown-log-toggle-${log.id}`}
                                >
                                  {`${log.items.length} פריטים`}
                                </UnstyledButton>
                              ) : (
                                '—'
                              )}
                            </span>
                          </div>
                          {expandedPrintLogIds.has(log.id) && log.items.length > 0 ? (
                            <div className={classes.logItemsExpandRow}>
                              <ul className={classes.logItemsList}>
                                {log.items.map((item, index) => (
                                  <li key={`${log.id}-${item.name}-${index}`} className={classes.logItemsListItem}>
                                    <span className={classes.logItemsListItemName}>
                                      <span className={classes.quantityInline}>{`x ${item.quantity}`}</span>
                                      {' '}
                                      {item.name}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : null}
                        </article>
                      ))}
                    </div>
                  </>
                )}
              </section>
            </div>
          </div>
        ) : null}
      </Modal>
    </Modal>
  );
};
